/* global algoliaSearchLocalizedData */
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
	searchBox,
	stats,
	hits,
	refinementList,
	clearRefinements,
} from 'instantsearch.js/es/widgets';

import { createDropdown } from './Dropdown';

// Set index name from localized data
if (typeof algoliaSearchBlockData !== 'undefined') {
	algoliaSearchLocalizedData.indexName =
		algoliaSearchBlockData['era_algolia_block_index'];
}

const searchClient = algoliasearch(
	algoliaSearchLocalizedData.appID,
	algoliaSearchLocalizedData.apiKey
);

const indexName = `${algoliaSearchLocalizedData.environment}_${algoliaSearchLocalizedData.indexName}`;

const search = instantsearch({
	indexName,
	searchClient,
	stalledSearchDelay: 0,
	routing: true,
});

// Fetch Manual Facet Terms from Algolia Settings page
// Will only pull the terms selected in the settings page.
const manualFacetSlugs = (window.algoliaSearchLocalizedData.manualFacetSlugs || []).map(
	String
);

/**
 * Algolia fallback image
 */
const fallbackImage = window.algoliaSearchBlockData.fallbackImage || '';
// const fallbackImageSrcset = window.algoliaSearchBlockData.fallbackImageSrcset || '';
/*console.log( 'fallbackImage:', fallbackImage );*/

// DEBUG
console.log('INITIAL LOCALIZED DATA: ', algoliaSearchLocalizedData);
console.log('BLOCK DATA: ', algoliaSearchBlockData);
console.log( 'DEFAULTVIEW: ' , window.algoliaSearchBlockData.defaultSections );
const masterDefaultViewPostTypes = window.algoliaSearchBlockData.defaultSections .flatMap( s => s.masterDefaultPosts );
console.log( 'MASTER DEFAULT VIEW : ', masterDefaultViewPostTypes );


// ----------------------
// Initial Results Section
// ----------------------
async function fetchInitialData() {
	const cfg = window.algoliaSearchBlockData.defaultSections || [];

	if (cfg.length > 0) {
		/*const queries = cfg.map(section => ({
			indexName,
			query: '',
			params: {
				hitsPerPage: section.hitsPerPage,
				filters: `post_type:${section.postType}`,
			},
		}));*/

		const queries = cfg.map((section, index) => ({
			indexName,
			query: '',
			params: {
				hitsPerPage: section.hitsPerPage,
				filters: `post_type:${
					section.defaultViewSelected === 'default' &&
					Array.isArray(section.masterDefaultPosts) &&
					section.masterDefaultPosts[index]
						? section.masterDefaultPosts[index]
						: section.postType
				}`,
			},
		}));

		const results = await searchClient.search(queries);

		const sections = cfg.map((section, i) => ({
			title: section.title,
			paragraph: section.paragraph,
			link: section.link,
			linkText: section.linkText,
			items:
				section.defaultViewSelected === 'manual' &&
				Array.isArray(section.manualPosts) &&
				section.manualPosts.length
					? section.manualPosts
					: section.defaultViewSelected === 'terms' &&
					Array.isArray(section.termPosts) &&
					section.termPosts.length
						? section.termPosts
						: results.results[i].hits,
		}));

		// --- NEW: sort each “default” section by publish_date desc ---
		sections.forEach(section => {
			if (section.defaultViewSelected === 'default') {
				section.items.sort((a, b) => {
					// ensure we have valid dates
					const da = new Date(a.publish_date).getTime() || 0;
					const db = new Date(b.publish_date).getTime() || 0;
					return db - da; // descending
				});
			}
		});

		renderInitialResults(sections);
		return;
	}

	const results = await searchClient.search([
		{
			indexName,
			query: '',
			params: { hitsPerPage: 3, filters: 'post_type:post' },
		},
		{
			indexName,
			query: '',
			params: { hitsPerPage: 3, filters: 'post_type:events' },
		},
		{
			indexName,
			query: '',
			params: { hitsPerPage: 3, filters: 'post_type:guides' },
		},
	]);

	renderInitialResults(results);
	console.log('ASYNC Results :', results);
}


// ----------------------
// Dynamic Facet Mounting
// ----------------------
const rawFacets = algoliaSearchBlockData.era_algolia_block_visible_facets || [];
const visibleFacets = rawFacets.map(f => (f === 'manual_facet' ? 'global_solutions' : f));
console.log('Selected facets for algolia block:', visibleFacets);

visibleFacets.forEach((facetSlug) => {
	const attribute = facetSlug;
	const isManual = facetSlug === 'global_solutions';
	const hyph = facetSlug.replace(/_/g, '-');

	let desktopSelector, mobileSelector;
	if (facetSlug === 'post_type') {
		desktopSelector = '#post-type';
		mobileSelector = '#facet-post-type-mobile';
	} else if (isManual) {
		desktopSelector = '#facet-global_solutions';
		mobileSelector = '#facet-global-solutions-mobile';
	} else {
		desktopSelector = `#facet-${facetSlug}`;
		mobileSelector = `#facet-${hyph}-mobile`;
	}

	[desktopSelector, mobileSelector].forEach((container) => {
		const widgetOpts = {
			container,
			attribute,
			sortBy: (a, b) => {
				if (facetSlug !== 'post_type') {
					return 0;
				}
				const aOrder = algoliaSearchBlockData.era_algolia_block_post_type_facet_order.find(
					(postType) => postType.name === a.name
				);
				let aOrderVal = typeof aOrder !== 'undefined' ? aOrder.order : 999;
				const bOrder = algoliaSearchBlockData.era_algolia_block_post_type_facet_order.find(
					(postType) => postType.name === b.name
				);
				let bOrderVal = typeof bOrder !== 'undefined' ? bOrder.order : 999;
				if (aOrderVal < bOrderVal) {
					return -1;
				}
				return 1;
			},
			showMoreText: ({ isShowingMore }) =>
				isShowingMore
					? algoliaSearchLocalizedData.translations.showLessFacets
					: algoliaSearchLocalizedData.translations.showMoreFacets,
			templates: {
				item(item, {html}){

					const {url, label, count, isRefined, value } = item;
					return html`<label class="facet-item">
            <input
              type="checkbox"
              value="${value}"
              checked=${isRefined}
              @change=${() => refine(value)}
            />
					<span class="facet-item__label">${getAttributeLabel(label, facetSlug)}</span>
          </label>
        `},
			},
		};



		if (isManual) {
			widgetOpts.transformItems = (items) =>
				items.filter((item) => manualFacetSlugs.includes(item.value));
			widgetOpts.sortBy = (a, b) =>
				manualFacetSlugs.indexOf(a.value) - manualFacetSlugs.indexOf(b.value);
		}

		if (facetSlug === 'post_type') {
			const visiblePostTypeOrder =
				algoliaSearchBlockData.era_algolia_block_post_type_facet_order || [];

			if (visiblePostTypeOrder.length) {
				widgetOpts.transformItems = (items) =>
					items.filter((item) => {
						return Boolean(
							visiblePostTypeOrder.find((postType) => postType.name === item.value)
						);
					});
			}

			widgetOpts.sortBy = (a, b) => {
				const aEntry = visiblePostTypeOrder.find((pt) => pt.name === a.name);
				const bEntry = visiblePostTypeOrder.find((pt) => pt.name === b.name);

				const aOrderVal = aEntry?.order ?? 999;
				const bOrderVal = bEntry?.order ?? 999;
				return aOrderVal - bOrderVal;
			};
		}

		const baseTransform = widgetOpts.transformItems;
		widgetOpts.transformItems = (items) => {
			const processed = baseTransform ? baseTransform(items) : items;
			const refined = processed.filter((i) => i.isRefined);
			if (refined.length > 0) {
				const unrefined = processed.filter((i) => !i.isRefined);
				return [...refined, ...unrefined];
			}
			return processed;
		};

		search.addWidget(refinementList(widgetOpts));
	});
});

function getAttributeLabel(label, facet_slug) {
	if (facet_slug === 'post_type' && algoliaSearchLocalizedData.translations.post_types.hasOwnProperty(label)) {
		return algoliaSearchLocalizedData.translations.post_types[label].default;
	}

	return label;
}

// ---------------------------
// Render Initial Results
// ---------------------------
function renderInitialResults(input) {
	const container = document.getElementById('initial-results');
	container.innerHTML = '';

	const sections = Array.isArray(input)
		? input
		: Array.isArray(window.algoliaSearchBlockData.defaultSections) &&
		window.algoliaSearchBlockData.defaultSections.length
			? window.algoliaSearchBlockData.defaultSections
		: [
			{
				title: algoliaSearchLocalizedData.translations['sectionTitlePrimary'],
				paragraph:
					algoliaSearchLocalizedData.translations['sectionParagraphPrimary'],
				link: '#',
				linkText: algoliaSearchLocalizedData.translations['sectionLinkText'],
				items: input.results[0].hits,
			},
			{
				title: algoliaSearchLocalizedData.translations['sectionTitleSecondary'],
				paragraph:
					algoliaSearchLocalizedData.translations['sectionParagraphSecondary'],
				link: '#',
				linkText: algoliaSearchLocalizedData.translations['sectionLinkText'],
				items: input.results[1].hits,
			},
			{
				title: algoliaSearchLocalizedData.translations['sectionTitleTertiary'],
				paragraph:
					algoliaSearchLocalizedData.translations['sectionParagraphTertiary'],
				link: '#',
				linkText: algoliaSearchLocalizedData.translations['sectionLinkText'],
				items: input.results[2].hits,
			},
		];

	/**
	 * Marketo form logic
	 * @type {*|string}
	 */

	const dynamicForm = window.algoliaSearchBlockData.marketoFormBlock || '';

	let newsletterLabel = algoliaSearchLocalizedData.translations['newsLetterLabel']
	let newsLetterTitle = algoliaSearchLocalizedData.translations['newsLetterTitle']

	const staticNewsData = window.algoliaSearchBlockData.link_to_default_view_newsletter || {};
	const {
		title: newsletterPreviewText    = algoliaSearchLocalizedData.translations['newsLetterPreview'],
		url:   newsletterPreviewUrl     = '#',
		target:newsletterPreviewTarget  = ''
	} = staticNewsData;

	const staticNewsletter = `
	<div class="era-algolia-newsletter-block has-background">
  <div class="era-algolia-newsletter-content">
    <div class="era-algolia-newsletter-text">
      <strong>${newsletterLabel}</strong>
      <h4 class="wp-block-heading">
       ${newsLetterTitle}
      </h4>
     <form class="newsletter-form">
<!--  <div class="newsletter-input-group">-->
<!--    <input type="email" placeholder="Enter your e-mail" required />-->
<!--    <div class="wp-block-button button-icon&#45;&#45;none is-style-icon button-size&#45;&#45;default">-->
<!--        <a class="wp-block-button__link has-black-color has-white-background-color has-text-color has-background has-link-color wp-element-button">-->
<!--            Subscribe-->
<!--        </a>-->
<!--    </div>-->
<!--  </div>-->
</form>
    </div>
    <div class="era-algolia-newsletter-image-container">
  <img src="https://conscia.com/wp-content/uploads/2022/04/sd-wan-linkedin.jpg" alt="Cybersecurity illustration" />
  <div class="newsletter-image-button">
    <div class="wp-block-button button-icon--none button-size--default">
      <a href="${newsletterPreviewUrl}" ${newsletterPreviewTarget ? `target="${newsletterPreviewTarget}" rel="noopener noreferrer"` : ''} class="wp-block-button__link has-white-color has-electric-blue-background-color has-text-color has-background has-link-color wp-element-button">
        <span>${newsletterPreviewText}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
<path d="M1.83301 8.58708C1.83301 8.58708 3.83301 3.92041 8.49967 3.92041C13.1663 3.92041 15.1663 8.58708 15.1663 8.58708C15.1663 8.58708 13.1663 13.2537 8.49967 13.2537C3.83301 13.2537 1.83301 8.58708 1.83301 8.58708Z" stroke="#FEF2F2" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.5 10.5869C9.60457 10.5869 10.5 9.69148 10.5 8.58691C10.5 7.48234 9.60457 6.58691 8.5 6.58691C7.39543 6.58691 6.5 7.48234 6.5 8.58691C6.5 9.69148 7.39543 10.5869 8.5 10.5869Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg> 
      </a>
    </div>
  </div>
</div>
  </div>
</div>
`;

	const newsletterBlock = dynamicForm || staticNewsletter;

	sections.forEach(({ title = '', paragraph = '', link = '', linkText = '', items }, index) => {
		if (items.length > 0) {
			const sectionEl = document.createElement('div');
			sectionEl.classList.add('era-algolia-section');

			sectionEl.innerHTML = `
			<header class="era-algolia-section-header">
				${title ? `<h2 class="wp-block-heading">${title}</h2>` : ''}
				<div class="era-algolia-section-header-inner">
					${paragraph ? `<p class="era-algolia-paragraph">${paragraph}</p>` : ''}
					${link ? `<div class="wp-block-button button-icon--none is-style-icon is-style-outline button-size--default view-all-button">
					<a href="${link}" class="wp-block-button__link wp-element-button">${linkText}</a></div>` : ''}
				</div>
			</header>
			<ul class="era-algolia-section-results">
				${items
				.map(
					(item) => `
						<li class="era-algolia-hit">
							<a href="${item.url}" class="post-card">
								<div class="post-card__media">
								<img
                                    src="${
									item.featured_image
									|| fallbackImage
									|| ''
									}"
                                    sizes="(max-width: 1024px) 100vw, 1024px"
                                    alt="${item.title}"
                                    class="post-card__img"
									/>
									<!--<div class="post-card__tags post-tags">
										<span class="post-tags__tag">TAG_NAME_1</span>
										<span class="post-tags__tag">TAG_NAME_2</span>
									</div>-->
								
								</div>
								<div class="post-meta">
									<span class="post-meta__post-type">${item.post_type}</span>
									${
									item.post_type === 'cases' || item.post_type === 'whitepapers'
									? ''
									: `<span class="post-meta__published">
									${
									item.id
									? new Date(item.publish_date).toLocaleDateString('en-GB')
									: new Date(item.publish_date).toLocaleDateString()}</span>`
									}
								</div>
								<h6 class="post-card__title">${item.title}</h6>
								<p class="post-card__excerpt">${item.excerpt}</p>
							</a>
						</li>
					`
				)
				.join('')}
			</ul>
		`;

			container.appendChild(sectionEl);

			if (index === 1) {
				const newsletterEl = document.createElement('div');
				newsletterEl.innerHTML = newsletterBlock;
				container.appendChild(newsletterEl);
			}
		}
	});
}

// ---------------------------
// InstantSearch Interactive UI
// ---------------------------
search.addWidgets([
	searchBox({
		container: '#searchbox',
		placeholder: algoliaSearchLocalizedData.translations['searchPlaceholder'],
	}),
	clearRefinements({
		container: '#clear-all-refinements',
		templates: {
			resetLabel: algoliaSearchLocalizedData.translations['clearAllFilters'],
		},
	}),
	clearRefinements({
		container: '#clear-all-refinements-mobile',
		templates: {
			resetLabel: algoliaSearchLocalizedData.translations['clearAllFilters'],
		},
	}),
	stats({
		container: '#algolia-stats',
		templates: {
			text: ({ nbHits }) => {
				const searchResultsText = algoliaSearchLocalizedData.translations;
				const label =
					nbHits === 1
						? `${searchResultsText.searchStatsPrefixSingular || 'View'} <span>1</span> ${
							searchResultsText.searchStatsSingular || 'result'
						}`
						: `${searchResultsText.searchStatsPrefixPlural ||
						'View all'} <span>${nbHits}</span> ${
							searchResultsText.countLabelPlural || 'results'
						}`;

				return `<div class="wp-block-button">
							<a role="button" class="algolia-stats-button wp-block-button__link has-white-color has-black-background-color has-text-color has-background has-link-color wp-element-button">
								${label} 
								<span class="algolia-stats-button-svg-element">
									<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
										<path d="M3.83398 8.33325H13.1673" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
										<path d="M8.5 3.66656L13.1667 8.33323L8.5 12.9999" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
									</svg>
								</span>
							</a>
						</div>`;
			},
		},
	}),
	stats({
		container: '#algolia-stats-desktop-count',
		templates: {
			text: ({ nbHits }) => {
				const translationString = algoliaSearchLocalizedData.translations;
				// singular label: countLabelSingular → countLabel → “Result”
				const singular = translationString.countLabelSingular || translationString.countLabel || 'Result';
				// plural   label: countLabelPlural   → countLabel   → “Results”
				const plural   = translationString.countLabelPlural   || translationString.countLabel   || 'Results';
				const label    = nbHits === 1 ? singular : plural;
				return `${nbHits} ${label}`;
			},
		},
	}),
	hits({
		container: '#hits',
		templates: {
			item: (hit) => `
				<a href="${hit.url}" class="post-card">
					<div class="post-card__media">
						<img
                        src="${
						hit.featured_image
						|| fallbackImage
						|| ''
						}"
                        sizes="(max-width: 1024px) 100vw, 1024px"
                        alt="${hit.title}"
                        class="post-card__img"
						/>
<!--						<div class="post-card__tags post-tags">-->
<!--							<span class="post-tags__tag">TAG_NAME_1</span>-->
<!--							<span class="post-tags__tag">TAG_NAME_2</span>-->
<!--						</div>-->
					</div>
					<div class="post-meta">
						<span class="post-meta__post-type">${hit.post_type}</span>
						<span class="post-meta__published">${
				hit.post_type === 'cases' || hit.post_type === 'whitepapers'
					? ''
					: `
					${hit.id ? new Date(hit.publish_date).toLocaleDateString('en-GB') : new Date(hit.publish_date).toLocaleDateString()}</span>`}</span>
					</div>
					<h6 class="post-card__title">${hit.title}</h6>
					<p class="post-card__excerpt">${hit.excerpt}</p>
				</a>
			`,
		},
	}),
]);

// ------------------------------
// Switch View on User Interaction
// ------------------------------
let hasInteracted = false;

search.on('render', () => {
	const state = search.helper.state;
	const query = state.query.trim();
	const hasRefinements = Object.values(state.disjunctiveFacetsRefinements).some(
		(arr) => arr.length > 0
	);

	const shouldShowInitialView = !query && !hasRefinements;

	if (shouldShowInitialView) {
		if (hasInteracted) {
			document.getElementById('initial-results').style.display = 'block';
			document.getElementById('main-results').style.display = 'none';
			fetchInitialData();
			hasInteracted = false;
		}
	} else {
		if (!hasInteracted) {
			document.getElementById('initial-results').style.display = 'none';
			document.getElementById('main-results').style.display = 'block';
			hasInteracted = true;
		}
	}

	const queryFeedbackEl = document.querySelector(
		'#era-algolia-search-query-feedback > h3'
	);
	if (queryFeedbackEl) {
		const algoliaSearchQuery = search.helper.state.query.trim();
		const prefix = algoliaSearchLocalizedData.translations.searchFeedbackPrefix;
		queryFeedbackEl.textContent = algoliaSearchQuery
			? `${prefix} '${algoliaSearchQuery}'`
			: '';
	}

	let currentRefinements = search.helper.state.disjunctiveFacetsRefinements;
	let activeFilters = -1;
	for (const [key, values] of Object.entries(currentRefinements)) {
		activeFilters += values.length;
	}
	const activeFiltersElement = document.getElementById('activeFilters');
	if (activeFiltersElement) {
		activeFiltersElement.textContent = activeFilters > 0 ? `(${activeFilters})` : '';
	}
});

// ------------------------
// Init
// ------------------------
fetchInitialData();
search.start();

if (typeof algoliaSearchBlockData !== 'undefined') {
	let preselected = algoliaSearchBlockData['era_algolia_block_preselected_attributes'] || [];
	if (preselected.length) {
		search.addWidget({
			init: (options) => {
				for (let i = 0; i < preselected.length; i++) {
					const split = preselected[i].split(':');
					options.helper.addDisjunctiveFacetRefinement(split[0], split[1]);
				}
			},
		});
	}

	let preselectedPostTypes = algoliaSearchBlockData['era_algolia_block_preselected_post_types'] || [];
	if (preselectedPostTypes.length) {
		search.addWidget({
			init: (options) => {
				for (let i = 0; i < preselectedPostTypes.length; i++) {
					options.helper.addDisjunctiveFacetRefinement('post_type', preselectedPostTypes[i]);
				}
			},
		});
	}
}

function facetHasPreSelectedRefinement(facet) {
	if (typeof algoliaSearchBlockData !== 'undefined') {
		let preselected = algoliaSearchBlockData['era_algolia_block_preselected_attributes'] || [];
		if (preselected.length) {
			for (let i = 0; i < preselected.length; i++) {
				const split = preselected[i].split(':');
				if (split[0] === facet) {
					return true;
				}
			}
		}
	}
	return false;
}

// Mobile filters logic…

/**
 * Mobile filters logic
 * TODO: Move to another file
 */

/* FACETS Mobile Filters Logic */

document.addEventListener('DOMContentLoaded', () => {
	const toggleButton = document.querySelector('.era-algolia-facet-toggle');
	const closeButton = document.getElementById('era-algolia-facet-close');
	const closeButtonSvg = document.querySelector('.era-algolia-facet-close > svg');
	const viewResultsButton = document.querySelector('.algolia-stats-button.mobile');
	const overlay = document.getElementById('post-type-overlay');
	const htmlElement = document.querySelector('html');
	const bodyElement = document.querySelector('body');

	if(toggleButton) {
		toggleButton.addEventListener('click', () => {
			// console.log( 'clicking' );
			overlay.classList.add('open');
			htmlElement.classList.add('no-scroll');
			bodyElement.classList.add('no-scroll');
		});
	}

	if(viewResultsButton) {
		viewResultsButton.addEventListener('click', () => {
			overlay.classList.remove('open');
			htmlElement.classList.remove('no-scroll');
			bodyElement.classList.remove('no-scroll');
		});
	}

	if (closeButton) {
		closeButton.addEventListener('click', () => {
			overlay.classList.remove('open');
			htmlElement.classList.remove('no-scroll');
			bodyElement.classList.remove('no-scroll');
		});
	}

	if(closeButtonSvg) {
		closeButtonSvg.addEventListener('click', () => {
			overlay.classList.remove('open');
			htmlElement.classList.remove('no-scroll');
			bodyElement.classList.remove('no-scroll');
		})
	}
});

/**
 * Mobile overlay view results button logic
 */
document.addEventListener('DOMContentLoaded', () => {
	document.addEventListener('click', function (event) {
		const button = event.target.closest('.algolia-stats-button');
		if (!button) return;

		// Close the overlay
		const overlay = document.getElementById('post-type-overlay');
		if (overlay) overlay.classList.remove('open');

		// Scroll to the results
		const results = document.getElementById('main-results');
		if (results) {
			results.scrollIntoView({behavior: 'smooth', block: 'start'});
		}
	});
});

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.era-algolia-facet-wrapper').forEach((wrapper) => {
		const toggleButton = wrapper.querySelector('.facet-toggle');
		if (!toggleButton) return;

		// Detect mobile wrappers
		const isMobile = wrapper.classList.contains('mobile');

		// Initialize state:
		// • mobile: collapsed + aria-expanded="false"
		// • desktop:    expanded + aria-expanded="true"
		wrapper.classList.toggle('is-collapsed', isMobile);
		toggleButton.setAttribute('aria-expanded', String(!isMobile));

		toggleButton.addEventListener('click', () => {
			const expanded = toggleButton.getAttribute('aria-expanded') === 'true';

			// Flip ARIA state
			toggleButton.setAttribute('aria-expanded', String(!expanded));
			// Toggle collapsed class
			wrapper.classList.toggle('is-collapsed', expanded);

			// Items inside
			const items = wrapper.querySelectorAll('.facet-item');

			if (!expanded) {
				// EXPANDING: staggered fade-in
				items.forEach((item, i) => {
					item.classList.remove('visible');
					setTimeout(() => item.classList.add('visible'), i * 50);
				});
			} else {
				// COLLAPSING: remove visibility
				items.forEach(item => item.classList.remove('visible'));
			}
		});
	});
});
